import { BehaviorSubject, Observable, Subscription, catchError, finalize, tap, throwError } from 'rxjs';
import { formatDate } from '@angular/common';
import { FieldMetadata, FilterMode, FilterType, MetadataModel, SearchFilter } from 'src/app/usage-api';
import { TableColumn, TableColumnRenders } from './datasource';
import { areArraysEqual } from '../../model/utility';

/**
 * Configuration for metadata loading
 */
export interface MetadataConfig<TModel extends object> {
  /** Function to load metadata from API */
  loadMetadata: () => Observable<MetadataModel>;
  /** Function to get localization key for a field */
  getLocalizationKey: (field: FieldMetadata) => string;
  /** Function to get column style for a field */
  getColumnStyle?: (field: FieldMetadata) => { [key: string]: string };
  /** Function to customize column after creation */
  customizeColumn?: (column: TableColumn<TModel>, field: FieldMetadata) => void;
  /** Date field names that should be converted to start/end in query params */
  dateRangeFields?: string[];
}

export class MetadataFilterManager<TModel extends object> {
  public readonly metadata$ = new BehaviorSubject<MetadataModel | null>(null);
  public readonly isMetadataLoaded$ = new BehaviorSubject<boolean>(false);
  public readonly columnsChanged$ = new BehaviorSubject<boolean>(false);
  public readonly searchFilters$ = new BehaviorSubject<SearchFilter[]>([]);
  public readonly isLoading$ = new BehaviorSubject<boolean>(false);

  private readonly dynamicFilters = new Map<string, BehaviorSubject<string[] | null>>();
  private columns: TableColumn<TModel>[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private config: MetadataConfig<TModel>) {}

  /**
   * Load metadata from API and create columns
   */
  public loadMetadata(config?: {
    subscription?: (subscription: Subscription) => void;
    next?: (value: MetadataModel) => void;
    error?: (error: unknown) => void;
  }): void {
    this.isLoading$.next(true);
    const subscription = this.config
      .loadMetadata()
      .pipe(
        tap((metadata: MetadataModel) => {
          this.metadata$.next(metadata);
          this.columns = this.createColumnsFromMetadata(metadata);
          this.isMetadataLoaded$.next(true);
          this.columnsChanged$.next(true);
          config?.next?.(metadata);
        }),
        catchError((error: unknown) => {
          console.error('Failed to load metadata:', error);
          config?.error?.(error);
          return throwError(() => error);
        }),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe();

    config?.subscription?.(subscription);
    this.subscriptions.push(subscription);
  }

  /**
   * Get the current columns
   */
  public getColumns(): TableColumn<TModel>[] {
    return this.columns;
  }

  /**
   * Update a dynamic filter
   */
  public updateFilter(
    field: string,
    values: string[] | null,
    mode: FilterMode = FilterMode.Includes,
    type: FilterType = FilterType.Values,
    onSubscribe?: (subscription: Subscription) => void,
  ): void {
    if (!this.dynamicFilters.has(field)) {
      this.dynamicFilters.set(field, new BehaviorSubject<string[] | null>(null));
      const filterSubject = this.dynamicFilters.get(field);
      if (filterSubject && onSubscribe) {
        // Caller needs to subscribe to filter changes
        const subscription = filterSubject.subscribe(() => {
          // Trigger data reload
        });
        onSubscribe(subscription);
      }
    }

    const filterSubject = this.dynamicFilters.get(field);
    if (filterSubject) {
      filterSubject.next(values);
    }

    this.updateSearchFilters(field, values, mode, type);
  }

  /**
   * Get current search filters
   */
  public getSearchFilters(): SearchFilter[] {
    return this.searchFilters$.value;
  }

  /**
   * Convert filters to query-friendly format for URL parameters
   */
  public queryFriendlyFilters(filters: SearchFilter[] | null | undefined): Record<string, string | null> {
    const result: Record<string, string | null> = {};
    const dateRangeFields = this.config.dateRangeFields || [];

    if (filters) {
      filters.forEach((filter) => {
        if (dateRangeFields.includes(filter.field || '')) {
          // Handle date range fields
          const startKey = `${filter.field}Start`;
          const endKey = `${filter.field}End`;
          result[startKey] = filter.values?.[0] ?? '';
          result[endKey] = filter.values?.[1] ?? '';
          try {
            if (result[startKey]) {
              result[startKey] = formatDate(new Date(result[startKey]), 'MM-dd-yyyy', 'en');
            }
            if (result[endKey]) {
              result[endKey] = formatDate(new Date(result[endKey]), 'MM-dd-yyyy', 'en');
            }
          } catch (error) {
            console.error('Error formatting date:', error);
          }
        } else if (filter.field) {
          // Handle regular fields
          result[filter.field] = filter.values?.join('|') ?? '';
        }
      });
    }

    // Clear removed filters
    this.dynamicFilters.forEach((_, fieldName) => {
      if (!filters?.some((f) => f.field === fieldName)) {
        result[fieldName] = null;
      }
    });

    return result;
  }

  /**
   * Compare two filter arrays for equality
   */
  public areFiltersEqual(filters1?: SearchFilter[] | null, filters2?: SearchFilter[] | null): boolean {
    if (!filters1 && !filters2) return true;
    if (!filters1 || !filters2) return false;
    if (filters1.length !== filters2.length) return false;

    return filters1.every((filter1, index) => {
      const filter2 = filters2[index];
      if (!filter2) {
        return false;
      }
      return (
        filter1.field === filter2.field &&
        filter1.mode === filter2.mode &&
        filter1.type === filter2.type &&
        areArraysEqual<string>(filter1.values, filter2.values)
      );
    });
  }

  /**
   * Clean up subscriptions
   */
  public destroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.dynamicFilters.forEach((subject) => subject.complete());
    this.metadata$.complete();
    this.isMetadataLoaded$.complete();
    this.columnsChanged$.complete();
    this.searchFilters$.complete();
  }

  private createColumnsFromMetadata(metadata: MetadataModel): TableColumn<TModel>[] {
    if (!metadata?.fields || !Array.isArray(metadata.fields)) {
      return [];
    }

    metadata.fields.sort((a, b) => (a.properties?.displayOrder ?? 999) - (b.properties?.displayOrder ?? 999));
    return metadata.fields.map((field) => this.createColumnFromField(field));
  }

  private createColumnFromField(field: FieldMetadata): TableColumn<TModel> {
    const fieldKey = field.key || '';
    const fieldType = field.type || '';

    const column: TableColumn<TModel> = {
      field: fieldKey,
      localizationKey: this.config.getLocalizationKey(field),
      style: this.config.getColumnStyle
        ? this.config.getColumnStyle(field)
        : {
            width: '8rem',
            'min-width': '8rem',
            'max-width': '12rem',
            'white-space': 'nowrap',
            'overflow-wrap': 'break-word',
          },
      noSort: field.properties?.sortable !== true,
      hideByDefault: field.properties?.displayOrder === -1,
      filterable: field.properties?.filterable === true,
    };

    // Set render function based on type
    switch (fieldType) {
      case 'date':
        column.render = (row) => TableColumnRenders.Date(row, fieldKey);
        break;
      case 'scaled_float':
      case 'double':
        column.render = (row) => TableColumnRenders.Currency(row, fieldKey, 4);
        break;
      default:
        column.render = (row) => TableColumnRenders.Reduce(row, fieldKey);
    }

    // Allow customization
    if (this.config.customizeColumn) {
      this.config.customizeColumn(column, field);
    }

    return column;
  }

  private updateSearchFilters(field: string, values: string[] | null, mode: FilterMode, type: FilterType): void {
    const currentFilters = this.searchFilters$.value;
    const existingFilterIndex = currentFilters.findIndex((f) => f.field === field);

    if ((values === null || values.length === 0) && existingFilterIndex !== -1) {
      const updatedFilters = [...currentFilters];
      updatedFilters.splice(existingFilterIndex, 1);
      this.searchFilters$.next(updatedFilters);
      return;
    }

    const newFilter: SearchFilter = {
      field,
      values,
      mode,
      type,
    };

    if (existingFilterIndex !== -1) {
      const updatedFilters = [...currentFilters];
      updatedFilters[existingFilterIndex] = newFilter;
      this.searchFilters$.next(updatedFilters);
    } else {
      this.searchFilters$.next([...currentFilters, newFilter]);
    }
  }
}
